import React, { useCallback } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { usePrefetch } from "../hooks/usePrefetch";

interface PrefetchLinkProps extends LinkProps {
  /**
   * Whether to prefetch the route chunk on hover/focus.
   * Defaults to true.
   */
  enablePrefetch?: boolean;
}

/**
 * A wrapper around React Router's Link that prefetches the target route's
 * lazy-loaded chunk when the user hovers or focuses the link.
 *
 * This reduces perceived navigation latency by pre-loading chunks before
 * the user actually clicks.
 *
 * Usage:
 * ```tsx
 * <PrefetchLink to="/dashboard">Dashboard</PrefetchLink>
 * ```
 */
export const PrefetchLink: React.FC<PrefetchLinkProps> = ({
  to,
  enablePrefetch = true,
  onMouseEnter,
  onFocus,
  onMouseLeave,
  onBlur,
  ...rest
}) => {
  const { prefetch, cancelPrefetch } = usePrefetch();

  const path = typeof to === "string" ? to : to.pathname ?? "";

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (enablePrefetch && path) {
        prefetch(path);
      }
      onMouseEnter?.(e);
    },
    [enablePrefetch, path, prefetch, onMouseEnter],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLAnchorElement>) => {
      if (enablePrefetch && path) {
        prefetch(path);
      }
      onFocus?.(e);
    },
    [enablePrefetch, path, prefetch, onFocus],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (enablePrefetch && path) {
        cancelPrefetch(path);
      }
      onMouseLeave?.(e);
    },
    [enablePrefetch, path, cancelPrefetch, onMouseLeave],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLAnchorElement>) => {
      if (enablePrefetch && path) {
        cancelPrefetch(path);
      }
      onBlur?.(e);
    },
    [enablePrefetch, path, cancelPrefetch, onBlur],
  );

  return (
    <Link
      to={to}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
      {...rest}
    />
  );
};

export default PrefetchLink;
